import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  IonContent,
  IonPage,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonButton,
  IonText,
  IonSpinner,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonIcon,
  IonRefresher,
  IonRefresherContent,
  IonToggle,
  type RefresherCustomEvent,
  type ToggleCustomEvent,
} from '@ionic/react';
import { createOutline, informationCircleOutline, enterOutline, trendingUpOutline, personRemoveOutline } from 'ionicons/icons';
import { useQuery } from '@apollo/client/react';
import { GET_TEAM_TAB_DATA } from '../../../graphql/queries/teamTabData';
import { GET_CURRENT_FANTASY_LEAGUE_MEMBER_ACTIVE_MARKET_ENTRIES } from '../../../graphql/queries/currentFantasyLeagueMemberActiveMarketEntries';
import { useUserAccount } from '../../../hooks/useUserAccount';
import { PlayerCard } from '../components/PlayerCard';
import { UserAvatar } from '../../components/UserAvatar';
import { LineupEditor } from './components/LineupEditor';
import { UpdateTeamNameModal } from './components/UpdateTeamNameModal';
import { AddPlayerPriceModal } from '../components/AddPlayerPriceModal';
import { IncreaseReleaseClauseModal } from './components/IncreaseReleaseClauseModal';
import { PlayerReleaseClauseChip } from '../components/PlayerReleaseClauseChip';
import { HelpModal } from '../components/HelpModal';
import { useRemovePlayerFromMarket } from '../hooks/useRemovePlayerFromMarket';
import { getTeamTabHelp } from '../../../constants/helpContent';
import { ROLE_LABELS } from '../services/LineupService';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { LeagueSettingsButton } from '../components/LeagueSettingsButton';
import { ContentSectionHeader } from '@/components/ui/ContentSectionHeader';
import { getPlayerRoleBarColor } from '../../../constants/playerRoleColors';
import type {
  GetTeamTabDataQuery,
  GetTeamTabDataQueryVariables,
  GetCurrentFantasyLeagueMemberActiveMarketEntriesQuery,
  GetCurrentFantasyLeagueMemberActiveMarketEntriesQueryVariables,
} from '../../../types/graphql-generated';

interface TeamTabProps {
  leagueId: string;
}

const COMPACT_MODE_STORAGE_KEY = 'playerCardCompactMode';

const readCompactModePreference = (): boolean => {
  try {
    return localStorage.getItem(COMPACT_MODE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

type FantasyTeamPlayer = NonNullable<
  NonNullable<GetTeamTabDataQuery['currentFantasyLeagueMember']>['fantasyTeamPlayers'][number]
>;

export const TeamTab = ({ leagueId }: TeamTabProps) => {
  const { userAccount } = useUserAccount();

  const location = useLocation();
  const teamPath = `/app/ligas/${leagueId}/equipo`;
  const previousPathnameRef = useRef(location.pathname);
  const wantsLineup = new URLSearchParams(location.search).get('seccion') === 'alineacion';

  const [selectedSegment, setSelectedSegment] = useState<'plantilla' | 'alineacion'>(
    wantsLineup ? 'alineacion' : 'plantilla'
  );

  // Honour deep links (e.g. from a lineup-reminder push notification) that ask to
  // open the "alineación" segment even when the tab is already mounted. Adjusting
  // state during render (React's recommended pattern) instead of in an effect.
  const [prevSearch, setPrevSearch] = useState(location.search);
  if (location.search !== prevSearch) {
    setPrevSearch(location.search);
    if (wantsLineup) {
      setSelectedSegment('alineacion');
    }
  }
  const [isEditNameModalOpen, setIsEditNameModalOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
  const [increaseClauseTarget, setIncreaseClauseTarget] = useState<FantasyTeamPlayer | null>(null);
  const [compactMode, setCompactMode] = useState<boolean>(readCompactModePreference);
  const { requestRemoveFromMarket, removeFromMarketAlert } = useRemovePlayerFromMarket(leagueId);

  const handleCompactModeChange = (value: boolean) => {
    setCompactMode(value);
    try {
      localStorage.setItem(COMPACT_MODE_STORAGE_KEY, String(value));
    } catch {
      // Ignore storage failures (e.g. private mode); the preference just won't persist.
    }
  };

  const { data, loading, error, refetch: refetchTeam } = useQuery<
    GetTeamTabDataQuery,
    GetTeamTabDataQueryVariables
  >(GET_TEAM_TAB_DATA, {
    variables: { fantasyLeagueId: leagueId },
    fetchPolicy: 'cache-and-network',
  });

  const { data: marketData, refetch: refetchMarketEntries } = useQuery<
    GetCurrentFantasyLeagueMemberActiveMarketEntriesQuery,
    GetCurrentFantasyLeagueMemberActiveMarketEntriesQueryVariables
  >(GET_CURRENT_FANTASY_LEAGUE_MEMBER_ACTIVE_MARKET_ENTRIES, {
    variables: { fantasyLeagueId: leagueId },
    fetchPolicy: 'cache-and-network',
  });

  // Ionic may retain this page while another league tab is active. Revalidate
  // when the user comes back so scheduled transfers are reflected immediately.
  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = location.pathname;

    if (location.pathname === teamPath && previousPathname !== teamPath) {
      void Promise.all([refetchTeam(), refetchMarketEntries()]).catch(() => undefined);
    }
  }, [location.pathname, refetchMarketEntries, refetchTeam, teamPath]);

  const handleRefresh = async (event: RefresherCustomEvent) => {
    try {
      await Promise.all([refetchTeam(), refetchMarketEntries()]);
    } finally {
      event.detail.complete();
    }
  };

  const currentMember = data?.currentFantasyLeagueMember;
  const league = currentMember?.fantasyLeague;

  const marketEntries = marketData?.currentFantasyLeagueMemberActiveMarketEntries || [];

  const marketEntryIdByPlayerId = new Map(
    marketEntries
      .map(entry => [entry.player.id, entry.id])
  );

  const isPlayerInMarket = (playerId: string): boolean => marketEntryIdByPlayerId.has(playerId);

  const groupPlayersByRole = () => {
    if (!currentMember?.fantasyTeamPlayers) return {};

    return [...currentMember.fantasyTeamPlayers]
      .sort((a, b) => (b.player.marketValue ?? 0) - (a.player.marketValue ?? 0))
      .reduce((acc, fantasyTeamPlayer) => {
        const role = fantasyTeamPlayer.player.role || 'Sin posición';
        if (!acc[role]) {
          acc[role] = [];
        }
        acc[role].push(fantasyTeamPlayer);
        return acc;
      }, {} as Record<string, typeof currentMember.fantasyTeamPlayers>);
  };

  const playersByRole = groupPlayersByRole();
  const totalMarketValue = currentMember?.fantasyTeamPlayers.reduce(
    (sum, fantasyTeamPlayer) => sum + (fantasyTeamPlayer.player.marketValue || 0),
    0
  ) || 0;

  const roleOrder = ['PORTERO', 'DEFENSA', 'CENTROCAMPISTA', 'DELANTERO', 'Sin posición'];
  function pluralizeRole(role: string): string {
    const label = ROLE_LABELS[role] || role;
    switch (role) {
      case 'PORTERO': return 'Porteros';
      case 'DEFENSA': return 'Defensas';
      case 'CENTROCAMPISTA': return 'Centrocampistas';
      case 'DELANTERO': return 'Delanteros';
      case 'Sin posición': return 'Sin posición';
      default: return label;
    }
  }

  const handleAddToMarket = (playerId: string) => {
    setSelectedPlayerId(playerId);
    setIsPriceModalOpen(true);
  };

  const handleModalClose = () => {
    setIsPriceModalOpen(false);
    setSelectedPlayerId(null);
  };

  const handleModalSuccess = () => {
    setIsPriceModalOpen(false);
    setSelectedPlayerId(null);
  };

  const handleRemoveFromMarket = (playerId: string) => {
    const marketEntryId = marketEntryIdByPlayerId.get(playerId);
    if (marketEntryId) {
      requestRemoveFromMarket(marketEntryId);
    }
  };

  const buildFooterActions = (fantasyTeamPlayer: FantasyTeamPlayer) => {
    const inMarket = isPlayerInMarket(fantasyTeamPlayer.player.id);
    const removeFromMarketAction = {
      label: 'Retirar del mercado',
      icon: personRemoveOutline,
      onClick: () => handleRemoveFromMarket(fantasyTeamPlayer.player.id),
      variant: 'danger' as const,
    };

    const increaseAction = {
      label: 'Aumentar cláusula',
      icon: trendingUpOutline,
      onClick: () => setIncreaseClauseTarget(fantasyTeamPlayer),
    };

    if (inMarket) {
      return [increaseAction, removeFromMarketAction];
    }

    return [
      {
        label: 'Añadir al mercado',
        icon: enterOutline,
        onClick: () => handleAddToMarket(fantasyTeamPlayer.player.id),
      },
      increaseAction,
    ];
  };

  const buildFooterDetails = (fantasyTeamPlayer: FantasyTeamPlayer) => {
    if (!isPlayerInMarket(fantasyTeamPlayer.player.id)) return undefined;
    return (
      <div className="flex items-center gap-2 text-slate-600">
        <IonIcon icon={enterOutline} className="text-base" />
        <span className="text-sm font-medium">Jugador en venta</span>
      </div>
    );
  };

  const selectedFantasyTeamPlayer = selectedPlayerId
    ? currentMember?.fantasyTeamPlayers.find(ftp => ftp.player.id === selectedPlayerId)
    : null;

  return (
    <IonPage>
      <AppSectionHeader variant="fantasy">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/main/mis-ligas" />
          </IonButtons>
          <IonTitle>{league?.name || 'Equipo'}</IonTitle>
          <IonButtons slot="end">
            <IonButton aria-label="Ayuda" onClick={() => setIsHelpModalOpen(true)}>
              <IonIcon slot="icon-only" icon={informationCircleOutline} />
            </IonButton>
            <LeagueSettingsButton leagueId={leagueId} />
          </IonButtons>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {loading && !data && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        )}

        {error && (
          <IonText color="danger">
            <p>Error al cargar el equipo</p>
          </IonText>
        )}

        {!loading && !currentMember && (
          <IonText>
            <p>No eres miembro de esta liga</p>
          </IonText>
        )}

        {currentMember && (
          <div className="space-y-4">
            <IonSegment
              value={selectedSegment}
              onIonChange={(e) => setSelectedSegment(e.detail.value as 'plantilla' | 'alineacion')}
            >
              <IonSegmentButton value="plantilla">
                <IonLabel>Plantilla</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="alineacion">
                <IonLabel>Alineación</IonLabel>
              </IonSegmentButton>
            </IonSegment>

            {selectedSegment === 'plantilla' && (
              <>
                <div className="rounded-2xl border border-[#d8ecdf] bg-gradient-to-b from-[#f0faf4] to-white p-3">
                  <div className="flex items-center gap-3">
                    <UserAvatar
                      picture={userAccount?.picture}
                      updatedAt={userAccount?.updatedAt}
                      name={userAccount?.fullName}
                      size={40}
                      className="ring-2 ring-white"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h2
                          className="truncate font-heading"
                          style={{ margin: 0, fontSize: '17px', fontWeight: 800, lineHeight: 1.15 }}
                        >
                          {currentMember.teamName || 'Mi Equipo'}
                        </h2>
                        <button
                          type="button"
                          aria-label="Editar nombre del equipo"
                          onClick={() => setIsEditNameModalOpen(true)}
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-transparent text-slate-500 transition-colors hover:bg-white/60"
                        >
                          <IonIcon icon={createOutline} className="text-base" />
                        </button>
                      </div>
                      <p className="mt-0.5 truncate text-xs text-slate-400">
                        Tu plantilla{league?.name ? ` · ${league.name}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex overflow-hidden rounded-xl border border-[#eaeff5] bg-white">
                    <div className="flex-1 px-3.5 py-2.5">
                      <div className="text-[10px] font-extrabold uppercase tracking-wide text-[var(--fc-ink-3)]">
                        Jugadores
                      </div>
                      <div className="font-heading text-[17px] font-extrabold">
                        {currentMember.fantasyTeamPlayers.length}
                      </div>
                    </div>
                    <div className="w-px bg-[#eaeff5]" />
                    <div className="flex-1 px-3.5 py-2.5">
                      <div className="text-[10px] font-extrabold uppercase tracking-wide text-[var(--fc-ink-3)]">
                        Valor total
                      </div>
                      <div className="font-heading text-[17px] font-extrabold">
                        {totalMarketValue.toLocaleString('es-ES')}€
                      </div>
                    </div>
                  </div>
                </div>

                {currentMember.fantasyTeamPlayers.length > 0 && (
                  <div className="mb-3 flex items-center justify-between rounded-xl border border-[#eaeff5] bg-white px-3.5 py-2">
                    <span className="text-sm font-semibold text-[var(--fc-ink-2)]">Modo compacto</span>
                    <IonToggle
                      checked={compactMode}
                      onIonChange={(event: ToggleCustomEvent) => handleCompactModeChange(event.detail.checked)}
                      aria-label="Activar modo compacto de las fichas de jugador"
                    />
                  </div>
                )}

                {roleOrder.map((role) => {
                  const fantasyTeamPlayers = playersByRole[role];
                  if (!fantasyTeamPlayers || fantasyTeamPlayers.length === 0) return null;

                  return (
                    <div key={role}>
                      <ContentSectionHeader
                        label={pluralizeRole(role)}
                        count={fantasyTeamPlayers.length}
                        barColor={getPlayerRoleBarColor(role)}
                        className="mb-2"
                      />
                      <div className="space-y-3">
                        {fantasyTeamPlayers.map((fantasyTeamPlayer) => (
                          <PlayerCard
                            key={fantasyTeamPlayer.id}
                            player={fantasyTeamPlayer.player}
                            compactMode={compactMode}
                            releaseClauseDisplay={
                              fantasyTeamPlayer.releaseClause != null ? (
                                <PlayerReleaseClauseChip
                                  releaseClause={fantasyTeamPlayer.releaseClause}
                                  individuallyProtected={fantasyTeamPlayer.individuallyProtected}
                                  weeklyWindowProtected={fantasyTeamPlayer.weeklyWindowProtected}
                                  signedAt={fantasyTeamPlayer.signedAt ?? fantasyTeamPlayer.createdAt}
                                />
                              ) : undefined
                            }
                            footerDetails={buildFooterDetails(fantasyTeamPlayer)}
                            footerActions={buildFooterActions(fantasyTeamPlayer)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}

                {currentMember.fantasyTeamPlayers.length === 0 && (
                  <IonText>
                    <p className="text-center text-gray-500 mt-8">
                      Aún no tienes jugadores en tu equipo
                    </p>
                  </IonText>
                )}
              </>
            )}

            {selectedSegment === 'alineacion' && (
              <LineupEditor
                leagueId={leagueId}
                memberId={currentMember.id}
                initialTactics={currentMember.tactics}
                fantasyTeamPlayers={currentMember.fantasyTeamPlayers}
              />
            )}
          </div>
        )}

        {selectedFantasyTeamPlayer && (
          <AddPlayerPriceModal
            isOpen={isPriceModalOpen}
            player={selectedFantasyTeamPlayer.player}
            leagueId={leagueId}
            source="team_tab"
            releaseClause={
              selectedFantasyTeamPlayer.releaseClause != null
                ? selectedFantasyTeamPlayer.releaseClause
                : undefined
            }
            onClose={handleModalClose}
            onSuccess={handleModalSuccess}
          />
        )}

        {increaseClauseTarget && currentMember && (
          <IncreaseReleaseClauseModal
            isOpen={increaseClauseTarget !== null}
            player={increaseClauseTarget.player}
            currentReleaseClause={increaseClauseTarget.releaseClause ?? 0}
            budget={currentMember.budget}
            leagueId={leagueId}
            onClose={() => setIncreaseClauseTarget(null)}
            onSuccess={() => setIncreaseClauseTarget(null)}
          />
        )}

        {removeFromMarketAlert}
      </IonContent>

      {currentMember && (
        <UpdateTeamNameModal
          isOpen={isEditNameModalOpen}
          onClose={() => setIsEditNameModalOpen(false)}
          memberId={currentMember.id}
          currentName={currentMember.teamName}
          leagueId={leagueId}
        />
      )}

      <HelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
        title={getTeamTabHelp().title}
        content={getTeamTabHelp().content}
      />
    </IonPage>
  );
};
